import {
  IDatabaseTableModel,
  IGenericSelectDataField,
  IGenericSelectDataModel,
  IGenericSelectWhereConditionModel
} from "@kuika/kuika-cl-model";
import { EManagedDbUpdateWhereType, ESymbolCategory, ISymbol } from "@kuika/kuika-cl-model/src/models/builder.dtos";
import _ from "lodash";
import * as React from "react";
import { DatasourceService } from "../../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaMenuItem from "../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSpinnerSmall from "../../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaTextBold from "../../../ui-kit/typography/TextBold/TextBold";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import { AddJoinModalComponent, ETreeType } from "./add-join-modal";
import { CodeViewComponent } from "./generic-sql-components/code-view";
import { ConvertToSqlConfirmationModal } from "./generic-sql-components/convert-to-sql-confirmation-modal";
import { FieldsComponent } from "./generic-sql-components/fields";
import { FilterComponent } from "./generic-sql-components/filter";
import { JoinTableComponent } from "./generic-sql-components/join-table";
import { SortOrderComponent } from "./generic-sql-components/sort-order";
import { SourcePanelComponent } from "./generic-sql-components/source-panel";
import { TableListComponent } from "./generic-sql-components/table-list";
import { TablePreviewComponent } from "./generic-sql-components/table-preview";

export interface IGenericSqlPanelState {
  addJoinTableName: string;
  addJoinTableSchema: string;
  isAddJoinModalVisible: boolean;
  testDataSource: any[];
  testColumns: any[];
  isConvertToSqlConfirmationVisible: boolean;
  sqlResult: string;
  isSourceDropdownVisible: boolean;
  isActionNameEditing: boolean;
  actionNameValue: string;
  activeTab: string;
  generatedSqlCode: string;
  isLoadingSqlCode: boolean;
  expandedTableKeys: string[];
  hasTested: boolean;
  isTesting: boolean;
}

export interface IGenericSqlPanelProps {
  datasourceId: string;
  nameValidator: (rule, value) => void;
  genericSelectData: IGenericSelectDataModel | undefined;
  updateGenericSelectData: (data: IGenericSelectDataModel) => void;
  convertToSql: (sqlString: string) => void;
  /**
   * The UI Design dialog. Only the dialog draws the name row below — `actionName`,
   * `onActionNameChange`, `startEditingName` and `nameHasError` are its; in the Datasources tool
   * the editor's bar owns the name.
   */
  isModal: boolean;
  actionName?: string;
  onActionNameChange?: (name: string) => void;
  footer?: React.ReactNode;
  startEditingName?: boolean;
  nameHasError?: boolean;
}

interface SymbolSqlParamTypes {
  sqlParamter: string;
  types: string[];
}

let isTableNameCheckDone = false;

class GenericSqlPanel extends React.PureComponent<IGenericSqlPanelProps & IWithSDProps, IGenericSqlPanelState> {
  testResultsRef = React.createRef<HTMLDivElement>();

  constructor(props: IGenericSqlPanelProps & IWithSDProps) {
    super(props);
    this.state = {
      addJoinTableName: "",
      addJoinTableSchema: "",
      isAddJoinModalVisible: false,
      testDataSource: [],
      testColumns: [],
      isConvertToSqlConfirmationVisible: false,
      sqlResult: "",
      isSourceDropdownVisible: false,
      isActionNameEditing: props.startEditingName || false,
      actionNameValue: "",
      activeTab: "relations",

      generatedSqlCode: "",
      isLoadingSqlCode: false,
      expandedTableKeys: [],
      hasTested: false,
      isTesting: false
    };
  }

  componentDidMount = () => {
    isTableNameCheckDone = false;
    if (this.props.genericSelectData === undefined) {
      this.updateSelectData({
        tables: [],
        fields: [],
        calculatedFields: [],
        relations: [],
        whereData: [],
        sortData: [],
        enablePagination: true
      } as IGenericSelectDataModel);
    } else {
      // Run validation on existing data
      this.updateSelectData(this.props.genericSelectData);
      this.updateTableNamesIfRequired();
    }
  };

  componentDidUpdate = () => {
    this.updateTableNamesIfRequired();
  };

  updateTableNamesIfRequired = () => {
    if (isTableNameCheckDone) return;
    const tables = this.getTables();
    if (tables.length === 0) {
      return;
    }
    isTableNameCheckDone = true;
    const selectData = _.cloneDeep(this.props.genericSelectData);
    if (!selectData) return;
    let isUpdateRequired = false;

    selectData.tables.forEach((x) => {
      const table = tables.find((t) => t.name === x.name);
      if (table && table.name !== x.name) {
        isUpdateRequired = true;
        x.name = table.name;
      }
    });

    selectData.relations.forEach((x) => {
      const fromTable = tables.find((t) => t.name === x.fromTable);
      if (fromTable && fromTable.name !== x.fromTable) {
        isUpdateRequired = true;
        x.fromTable = fromTable.name;
      }

      const toTable = tables.find((t) => t.name === x.toTable);
      if (toTable && toTable.name !== x.toTable) {
        isUpdateRequired = true;
        x.toTable = toTable.name;
      }
    });

    selectData.fields.forEach((x) => {
      const table = tables.find((t) => t.name === x.tableName);
      if (table && table.name !== x.tableName) {
        isUpdateRequired = true;
        x.tableName = table.name;
      }
      x.tableSchema = table?.schema ?? "";
    });

    if (selectData.calculatedFields) {
      selectData.calculatedFields.forEach((calcField) => {
        // Update fields
        if (calcField.fields) {
          calcField.fields.forEach((f) => {
            const table = tables.find((t) => t.name === f.tableName);
            if (table && table.name !== f.tableName) {
              isUpdateRequired = true;
              f.tableName = table.name;
            }
            f.tableSchema = table?.schema ?? "";
          });
        }
        // Update partitionByFields
        if (calcField.partitionByFields) {
          calcField.partitionByFields.forEach((f) => {
            const table = tables.find((t) => t.name === f.tableName);
            if (table && table.name !== f.tableName) {
              isUpdateRequired = true;
              f.tableName = table.name;
            }
            f.tableSchema = table?.schema ?? "";
          });
        }
        // Update orderByFields
        if (calcField.orderByFields) {
          calcField.orderByFields.forEach((f) => {
            const table = tables.find((t) => t.name === f.tableName);
            if (table && table.name !== f.tableName) {
              isUpdateRequired = true;
              f.tableName = table.name;
            }
            f.tableSchema = table?.schema ?? "";
          });
        }
      });
    }

    selectData.whereData.forEach((x) => {
      if (x.type === EManagedDbUpdateWhereType.and && x.andCondition) {
        const table = tables.find((t) => t.name === x.andCondition?.tableName);
        if (table && table.name !== x.andCondition.tableName) {
          isUpdateRequired = true;
          x.andCondition.tableName = table.name;
        }
      } else if (x.type === EManagedDbUpdateWhereType.orGroup && x.orConditions) {
        x.orConditions.forEach((orCondition) => {
          const table = tables.find((t) => t.name === orCondition.tableName);
          if (table && table.name !== orCondition.tableName) {
            isUpdateRequired = true;
            orCondition.tableName = table.name;
          }
        });
      }
    });

    selectData.sortData.forEach((x) => {
      const table = tables.find((t) => t.name === x.tableName);
      if (table && table.name !== x.tableName) {
        isUpdateRequired = true;
        x.tableName = table.name;
      }
      x.tableSchema = table?.schema ?? "";
    });

    if (isUpdateRequired) this.updateSelectData(selectData);
  };

  getTables = (): IDatabaseTableModel[] => {
    const datasource = this.props.sd?.schemas.get(this.props.datasourceId ?? "");
    const tables = datasource?.tables ?? [];
    const defaultKuikaTableNames = KMainFunctions.getDefaultKuikaTableNames();
    const filteredTables = tables; // .filter((x) => defaultKuikaTableNames.indexOf(x.name) === -1);
    return filteredTables as IDatabaseTableModel[];
  };

  findTableId = (tableSchema, tableName: string): string => {
    const tables = this.getTables();
    const table = tables.find((x) => x.name === tableName && x.schema === tableSchema);
    return table?.id ?? "";
  };

  findTableSchema = (tableName: string): string => {
    const tables = this.getTables();
    const table = tables.find((x) => x.name === tableName);
    return table?.schema ?? "";
  };

  handleSelectTable = (tableSchema: string, tableName: string) => {
    const newData = {
      tables: [
        {
          id: this.findTableId(tableSchema, tableName) == "" ? null : this.findTableId(tableSchema, tableName),
          name: tableName,
          schema: this.findTableSchema(tableName)
        }
      ],
      fields: this.getDefaultFieldsForTable(tableSchema, tableName),
      calculatedFields: [],
      relations: [],
      whereData: [],
      sortData: []
    } as IGenericSelectDataModel;
    this.updateSelectData(newData);
    // Expand all panels after adding source
    this.expandAllTables(newData);
  };

  getDefaultFieldsForTable = (
    tableSchema: string,
    tableName: string,
    tableAlias?: string
  ): IGenericSelectDataField[] => {
    const tables = this.getTables();
    const selectedTable = tables.find((x) => x.name === tableName);
    if (selectedTable) {
      const stringCols = selectedTable.columns.filter(
        (x) => x.sqlDataType === "nvarchar" || x.sqlDataType == "NVARCHAR2"
      );
      if (stringCols.length > 0) {
        return [
          {
            tableName,
            tableSchema: selectedTable.schema,
            tableId: this.findTableId(tableSchema, tableName) == "" ? null : this.findTableId(tableSchema, tableName),
            tableAlias,
            colName: stringCols[0].name,
            colSqlDataType: stringCols[0].sqlDataType,
            alias: ""
          }
        ];
      }
    }
    return [];
  };

  handleAddJoins = (dataToAdd: IGenericSelectDataModel, treeType: ETreeType) => {
    const selectData = _.cloneDeep(this.props.genericSelectData as IGenericSelectDataModel);

    dataToAdd.tables.forEach((t, idx) => {
      // Get the corresponding relation to know which FK this table is joined via
      const relation = dataToAdd.relations[idx];
      const joinedViaFk = relation
        ? treeType === ETreeType.Parent
          ? relation.fromFk // Parent: FK is in the origin table
          : relation.fromFk // Child: FK is in the child table
        : undefined;

      // Check if this exact table+FK combination already exists
      const existingTable = selectData.tables.find((x) => x.name === t.name && x.joinedViaFk === joinedViaFk);

      if (!existingTable) {
        // Check if same table exists with a different FK (multiple joins to same table)
        const sameTableDifferentFk = selectData.tables.find((x) => x.name === t.name && x.joinedViaFk !== joinedViaFk);

        // Create alias if this is a duplicate table joined via different FK
        const needsAlias = sameTableDifferentFk || joinedViaFk;
        const alias = needsAlias ? (joinedViaFk ? `${t.name}[${joinedViaFk}]` : t.name) : undefined;

        selectData.tables.push({
          id: t.id,
          name: t.name,
          schema: t.schema,
          alias,
          joinedViaFk
        });

        // Add default fields for the new table with proper alias reference
        const newFields = this.getDefaultFieldsForTable(t.schema, t.name, alias);
        selectData.fields = selectData.fields.concat(newFields);

        // If there was a previous entry without alias, update it to have an alias too
        if (sameTableDifferentFk && !sameTableDifferentFk.alias) {
          if (sameTableDifferentFk.joinedViaFk)
            sameTableDifferentFk.alias = `${sameTableDifferentFk.name}[${sameTableDifferentFk.joinedViaFk}]`;
          else sameTableDifferentFk.alias = sameTableDifferentFk.name;
          // Update existing fields that reference this table
          selectData.fields.forEach((f) => {
            if (f.tableName === sameTableDifferentFk.name && !f.tableAlias) {
              f.tableAlias = sameTableDifferentFk.alias;
            }
          });
        }
      }
    });

    dataToAdd.relations.forEach((r) => {
      // Check if this specific FK relationship already exists (not just table-based)
      const relationExists = selectData.relations.some(
        (x) => x.fromFk === r.fromFk && x.fromTable === r.fromTable && x.toTable === r.toTable && x.toPk === r.toPk
      );
      if (!relationExists) {
        selectData.relations.push(r);
      }
    });

    this.updateSelectData(selectData);
    this.setState({ isAddJoinModalVisible: false });
    // Expand all panels after adding joins
    this.expandAllTables(selectData);
  };

  expandAllTables = (data?: IGenericSelectDataModel) => {
    const selectData = data || this.props.genericSelectData;
    const tableNames = selectData?.tables.map((t) => t.name) || [];
    this.setState({ expandedTableKeys: tableNames });
  };

  updateSelectData = (data: IGenericSelectDataModel) => {
    // Validations...
    this.runTableValidations(data);
    this.runFieldValidations(data);
    this.runCalculatedFieldValidations(data);
    this.runRelationsValidations(data);
    this.runWhereDataValidations(data);
    this.runSortDataValidations(data);
    data.hasValidationError = this.hasValidationError(data);
    this.props.updateGenericSelectData(data);
  };

  runTableValidations = (data: IGenericSelectDataModel) => {
    data.tables.forEach((x) => {
      if (!x.validations) x.validations = {};
      x.validations.name = this.isValidString(x.name);
    });
  };

  runFieldValidations = (data: IGenericSelectDataModel) => {
    if (!data.fields) data.fields = [];
    data.fields.forEach((x) => {
      if (!x.validations) x.validations = {};
      x.validations.tableName = this.isValidString(x.tableName);
      x.validations.colName = this.isValidString(x.colName);
      x.validations.alias = !x.groupBy || x.alias?.length > 0;
    });
  };

  runCalculatedFieldValidations = (data: IGenericSelectDataModel) => {
    if (!data.calculatedFields) data.calculatedFields = [];
    // ECalculatedFieldFunction: DateDiff = 1, RowNumber = 2
    data.calculatedFields.forEach((x) => {
      if (!x.validations) x.validations = {};
      x.validations.alias = this.isValidString(x.alias);
      x.validations.function = x.function !== undefined && x.function > 0;

      // Validate fields for DateDiff (needs 2 fields)
      if (x.function === 1) {
        // DateDiff
        x.validations.fields =
          x.fields &&
          x.fields.length === 2 &&
          x.fields.every((f) => {
            // Check if it's a valid table field
            const isValidTableField = this.isValidString(f.tableName) && this.isValidString(f.colName);
            // Check if it's a valid fixed value (either by isFixed flag or category)
            const isValidFixedValue =
              (f.paramSymbol?.isFixed === true || f.paramSymbol?.category === ESymbolCategory.fixed) &&
              f.paramSymbol?.fixedValue != null &&
              f.paramSymbol?.fixedValue !== "";
            // Check if it's a valid default value (like "now", "empty", etc.)
            const isValidDefaultValue =
              f.paramSymbol?.category === ESymbolCategory.default &&
              f.paramSymbol?.defaultValueName != null &&
              f.paramSymbol?.defaultValueName !== "";
            // Check if it's a valid SQL parameter
            const isValidSqlParameter =
              f.paramSymbol?.category === ESymbolCategory.sqlParameter &&
              ((f.paramSymbol?.sqlParamName != null && f.paramSymbol?.sqlParamName !== "") ||
                (f.paramSymbol?.label != null && f.paramSymbol?.label !== ""));
            // Check if it's a valid action field (table field selected from symbol picker)
            const isValidActionField =
              f.paramSymbol?.category === ESymbolCategory.actionField &&
              f.paramSymbol?.fieldName != null &&
              f.paramSymbol?.fieldName !== "";
            return (
              isValidTableField || isValidFixedValue || isValidDefaultValue || isValidSqlParameter || isValidActionField
            );
          });
      } else {
        x.validations.fields = true; // RowNumber doesn't use fields array
      }

      // Validate orderByFields for RowNumber function
      if (x.function === 2) {
        // RowNumber
        x.validations.orderByFields =
          x.orderByFields &&
          x.orderByFields.length > 0 &&
          x.orderByFields.every((f) => {
            // Check if it's a valid table field
            const isValidTableField = this.isValidString(f.tableName) && this.isValidString(f.colName);
            // Check if it's a valid SQL parameter
            const isValidSqlParameter =
              f.paramSymbol?.category === ESymbolCategory.sqlParameter &&
              ((f.paramSymbol?.sqlParamName != null && f.paramSymbol?.sqlParamName !== "") ||
                (f.paramSymbol?.label != null && f.paramSymbol?.label !== ""));
            // Check if it's a valid action field (table field selected from symbol picker)
            const isValidActionField =
              f.paramSymbol?.category === ESymbolCategory.actionField &&
              f.paramSymbol?.fieldName != null &&
              f.paramSymbol?.fieldName !== "";
            return (isValidTableField || isValidSqlParameter || isValidActionField) && (f.sortType ?? 0) > 0;
          });
      } else {
        x.validations.orderByFields = true; // DateDiff doesn't need orderBy
      }

      // Validate fields within each field array (using IGenericSelectDataField)
      if (x.fields) {
        x.fields.forEach((f) => {
          if (!f.validations) f.validations = {};
          // Check if it's a valid fixed value (either by isFixed flag or category)
          const isFixedValue =
            (f.paramSymbol?.isFixed || f.paramSymbol?.category === ESymbolCategory.fixed) &&
            f.paramSymbol?.fixedValue != null &&
            f.paramSymbol?.fixedValue !== "";
          // Check if it's a valid default value (like "now", "empty", etc.)
          const isDefaultValue =
            f.paramSymbol?.category === ESymbolCategory.default &&
            f.paramSymbol?.defaultValueName != null &&
            f.paramSymbol?.defaultValueName !== "";
          // Check if it's a valid SQL parameter
          const isSqlParameter =
            f.paramSymbol?.category === ESymbolCategory.sqlParameter &&
            ((f.paramSymbol?.sqlParamName != null && f.paramSymbol?.sqlParamName !== "") ||
              (f.paramSymbol?.label != null && f.paramSymbol?.label !== ""));
          // Check if it's a valid action field (table field selected from symbol picker)
          const isActionField =
            f.paramSymbol?.category === ESymbolCategory.actionField &&
            f.paramSymbol?.fieldName != null &&
            f.paramSymbol?.fieldName !== "";
          f.validations.tableName =
            this.isValidString(f.tableName) || isFixedValue || isDefaultValue || isSqlParameter || isActionField;
          f.validations.colName =
            this.isValidString(f.colName) || isFixedValue || isDefaultValue || isSqlParameter || isActionField;
        });
      }

      // Validate partitionBy fields (using IGenericSelectDataField)
      if (x.partitionByFields) {
        x.partitionByFields.forEach((f) => {
          if (!f.validations) f.validations = {};
          const isFixedValue =
            (f.paramSymbol?.isFixed || f.paramSymbol?.category === ESymbolCategory.fixed) &&
            f.paramSymbol?.fixedValue != null &&
            f.paramSymbol?.fixedValue !== "";
          const isDefaultValue =
            f.paramSymbol?.category === ESymbolCategory.default &&
            f.paramSymbol?.defaultValueName != null &&
            f.paramSymbol?.defaultValueName !== "";
          // Check if it's a valid SQL parameter
          const isSqlParameter =
            f.paramSymbol?.category === ESymbolCategory.sqlParameter &&
            ((f.paramSymbol?.sqlParamName != null && f.paramSymbol?.sqlParamName !== "") ||
              (f.paramSymbol?.label != null && f.paramSymbol?.label !== ""));
          // Check if it's a valid action field (table field selected from symbol picker)
          const isActionField =
            f.paramSymbol?.category === ESymbolCategory.actionField &&
            f.paramSymbol?.fieldName != null &&
            f.paramSymbol?.fieldName !== "";
          f.validations.tableName =
            this.isValidString(f.tableName) || isFixedValue || isDefaultValue || isSqlParameter || isActionField;
          f.validations.colName =
            this.isValidString(f.colName) || isFixedValue || isDefaultValue || isSqlParameter || isActionField;
        });
      }

      // Validate orderBy fields (using IGenericSelectDataField with sortType)
      if (x.orderByFields) {
        x.orderByFields.forEach((f) => {
          if (!f.validations) f.validations = {};
          // Check if it's a valid SQL parameter
          const isSqlParameter =
            f.paramSymbol?.category === ESymbolCategory.sqlParameter &&
            ((f.paramSymbol?.sqlParamName != null && f.paramSymbol?.sqlParamName !== "") ||
              (f.paramSymbol?.label != null && f.paramSymbol?.label !== ""));
          // Check if it's a valid action field (table field selected from symbol picker)
          const isActionField =
            f.paramSymbol?.category === ESymbolCategory.actionField &&
            f.paramSymbol?.fieldName != null &&
            f.paramSymbol?.fieldName !== "";
          f.validations.tableName = this.isValidString(f.tableName) || isSqlParameter || isActionField;
          f.validations.colName = this.isValidString(f.colName) || isSqlParameter || isActionField;
          f.validations.sortType = (f.sortType ?? 0) > 0;
        });
      }
    });
  };

  runRelationsValidations = (data: IGenericSelectDataModel) => {
    data.relations.forEach((x) => {
      if (!x.validations) x.validations = {};
      x.validations.fromTable = this.isValidString(x.fromTable);
      x.validations.fromFk = this.isValidString(x.fromFk);
      x.validations.toTable = this.isValidString(x.toTable);
      x.validations.joinType = x.joinType > 0;
    });
  };

  runWhereDataValidations = (data: IGenericSelectDataModel) => {
    let sqlParamTypes: SymbolSqlParamTypes[] = [];
    data.whereData.forEach((x) => {
      if (x.type === EManagedDbUpdateWhereType.and && x.andCondition) {
        if (!x.andCondition.validations) x.andCondition.validations = {};
        x.andCondition.validations.tableName = this.isValidString(x.andCondition.tableName);
        x.andCondition.validations.colName = this.isValidString(x.andCondition.colName);
        x.andCondition.validations.conditionOperator = this.isValidString(x.andCondition.conditionOperator);
        x.andCondition.validations.paramSymbol = this.isValidSymbol(x.andCondition.paramSymbol);
        this.addSymbolToParamTypes(sqlParamTypes, x.andCondition);
      } else if (x.type === EManagedDbUpdateWhereType.orGroup && x.orConditions) {
        x.orConditions.forEach((orCondition) => {
          if (!orCondition.validations) orCondition.validations = {};
          orCondition.validations.tableName = this.isValidString(orCondition.tableName);
          orCondition.validations.colName = this.isValidString(orCondition.colName);
          orCondition.validations.conditionOperator = this.isValidString(orCondition.conditionOperator);
          orCondition.validations.paramSymbol = this.isValidSymbol(orCondition.paramSymbol);
          this.addSymbolToParamTypes(sqlParamTypes, orCondition);
        });
      }
    });
    this.handleInvalidSqlParamTypes(data, sqlParamTypes);
  };

  handleInvalidSqlParamTypes = (data: IGenericSelectDataModel, sqlParamTypes: SymbolSqlParamTypes[]) => {
    const incorrectSqlParams: string[] = [];
    sqlParamTypes.forEach((sqlParamType) => {
      if (_.uniq(sqlParamType.types).length > 1) {
        incorrectSqlParams.push(sqlParamType.sqlParamter);
      }
    });

    data.whereData.forEach((x) => {
      if (x.type === EManagedDbUpdateWhereType.and && x.andCondition) {
        if (!x.andCondition.validations) x.andCondition.validations = {};
        this.addSymbolToParamTypes(sqlParamTypes, x.andCondition);
        if (incorrectSqlParams.indexOf(x.andCondition.paramSymbol.sqlParamName ?? "") > -1) {
          x.andCondition.validations.paramSymbol = false;
        }
      } else if (x.type === EManagedDbUpdateWhereType.orGroup && x.orConditions) {
        x.orConditions.forEach((orCondition) => {
          if (!orCondition.validations) orCondition.validations = {};
          if (incorrectSqlParams.indexOf(orCondition.paramSymbol.sqlParamName ?? "") > -1) {
            orCondition.validations.paramSymbol = false;
          }
        });
      }
    });
  };

  addSymbolToParamTypes = (sqlParamTypes: SymbolSqlParamTypes[], conditionModel: IGenericSelectWhereConditionModel) => {
    const symbol = conditionModel.paramSymbol;
    const sqlParamName = symbol.sqlParamName;
    if (sqlParamName && sqlParamName.length > 0) {
      const itemInArr = sqlParamTypes.find((x) => x.sqlParamter === sqlParamName);
      if (itemInArr) {
        itemInArr.types.push(conditionModel.colSqlDataType);
      } else {
        sqlParamTypes.push({
          sqlParamter: sqlParamName,
          types: [conditionModel.colSqlDataType]
        });
      }
    }
  };

  runSortDataValidations = (data: IGenericSelectDataModel) => {
    data.sortData.forEach((x) => {
      if (!x.validations) x.validations = {};
      x.validations.tableName = this.isValidString(x.tableName);
      x.validations.colName = this.isValidString(x.colName);
      x.validations.sortType = x.sortType > 0;
    });
  };

  hasValidationError = (data: IGenericSelectDataModel): boolean => {
    let hasValidationError = false;

    if (this.isRelationsDataHasValidationError(data)) {
      hasValidationError = true;
    }

    // Fields tab now includes both regular fields and calculated fields
    if (this.isFieldsDataHasValidationError(data) || this.isCalculatedFieldsDataHasValidationError(data)) {
      hasValidationError = true;
    }

    if (this.isWhereDataHasValidationError(data)) {
      hasValidationError = true;
    }

    if (this.isSortDataHasValidationError(data)) {
      hasValidationError = true;
    }

    return hasValidationError;
  };

  isRelationsDataHasValidationError = (data: IGenericSelectDataModel): boolean => {
    let hasValidationError = false;
    data.relations.forEach((x) => {
      if (x.validations && Object.values(x.validations as any).some((v) => v === false)) hasValidationError = true;
    });
    return hasValidationError;
  };

  isFieldsDataHasValidationError = (data: IGenericSelectDataModel): boolean => {
    let hasValidationError = false;
    data.fields.forEach((x) => {
      if (x.validations && Object.values(x.validations as any).some((v) => v === false)) hasValidationError = true;
    });
    return hasValidationError;
  };

  isCalculatedFieldsDataHasValidationError = (data: IGenericSelectDataModel): boolean => {
    let hasValidationError = false;
    if (!data.calculatedFields) return false;
    data.calculatedFields.forEach((x) => {
      // Check main validations
      if (x.validations && Object.values(x.validations as any)?.some((v) => v === false)) {
        hasValidationError = true;
      }
      // Check fields validations
      if (x.fields) {
        x.fields.forEach((f) => {
          if (f.validations && Object.values(f.validations as any)?.some((v) => v === false)) {
            hasValidationError = true;
          }
        });
      }
      // Check partitionBy validations
      if (x.partitionByFields) {
        x.partitionByFields.forEach((f) => {
          if (f.validations && Object.values(f.validations as any)?.some((v) => v === false)) {
            hasValidationError = true;
          }
        });
      }
      // Check orderBy validations
      if (x.orderByFields) {
        x.orderByFields.forEach((f) => {
          if (f.validations && Object.values(f.validations as any)?.some((v) => v === false)) {
            hasValidationError = true;
          }
        });
      }
    });
    return hasValidationError;
  };

  isWhereDataHasValidationError = (data: IGenericSelectDataModel): boolean => {
    let hasValidationError = false;
    data.whereData.forEach((x) => {
      if (x.type === EManagedDbUpdateWhereType.and && x.andCondition) {
        if (x.andCondition.validations && Object.values(x.andCondition.validations as any).some((v) => v === false))
          hasValidationError = true;
      } else if (x.type === EManagedDbUpdateWhereType.orGroup && x.orConditions) {
        x.orConditions.forEach((orCondition) => {
          if (orCondition.validations && Object.values(orCondition.validations as any).some((v) => v === false))
            hasValidationError = true;
        });
      }
    });
    return hasValidationError;
  };

  isSortDataHasValidationError = (data: IGenericSelectDataModel): boolean => {
    let hasValidationError = false;

    // If pagination is enabled, at least one sort order is required
    if (data.enablePagination && (!data.sortData || data.sortData.length === 0)) {
      hasValidationError = true;
    }

    data.sortData.forEach((x) => {
      if (x.validations && Object.values(x.validations as any).some((v) => v === false)) hasValidationError = true;
    });
    return hasValidationError;
  };

  isValidString = (string: string | undefined) => {
    if (string === undefined) return false;
    if (string?.trim()?.length > 0) return true;
    return false;
  };

  isValidSymbol = (symbol: ISymbol | undefined) => {
    if (!symbol) return false;
    const emptySymbol = { category: ESymbolCategory.fixed } as ISymbol;
    if (_.isEqual(symbol, emptySymbol)) return false;
    if (symbol.category === ESymbolCategory.fixed && typeof symbol.fixedValue === "boolean") return true;
    if (symbol.category === ESymbolCategory.fixed && !this.isValidString(symbol.fixedValue)) return false;
    if (symbol.category === ESymbolCategory.sqlParameter && !this.isValidString(symbol.sqlParamName)) return false;
    return true;
  };

  handleClickTest = async () => {
    this.setState({ hasTested: true, isTesting: true });
    try {
      const res = await DatasourceService.TestGenericSelect(
        this.props.datasourceId,
        this.props.genericSelectData as IGenericSelectDataModel
      );
      const data = res.data;
      if (!data) return;
      const testColumns: any[] = [];

      if (data.length > 0) {
        for (const [key, value] of Object.entries(data[0])) {
          testColumns.push({
            title: key,
            dataIndex: key,
            key,
            value
          });
        }
      }

      this.setState({ testDataSource: data, testColumns });
    } finally {
      this.setState({ isTesting: false });
    }
  };

  handleClickConvertToSql = async () => {
    const res = await DatasourceService.GetGenericSelectDataSql(
      this.props.datasourceId,
      this.props.genericSelectData as IGenericSelectDataModel
    );
    const data = res.data;
    if (!data) return;
    this.setState({ isConvertToSqlConfirmationVisible: true, sqlResult: data });
  };

  convertToSql = () => {
    this.props.convertToSql(this.state.sqlResult);
  };

  handleActionNameClick = () => {
    this.setState({
      isActionNameEditing: true,
      actionNameValue: this.props.actionName || ""
    });
  };

  handleActionNameCancel = () => {
    this.setState({
      isActionNameEditing: false,
      actionNameValue: this.props.actionName || ""
    });
  };

  handleActionNameSave = () => {
    if (this.props.onActionNameChange) {
      this.props.onActionNameChange(this.state.actionNameValue);
    }
    this.setState({ isActionNameEditing: false });
  };

  handleActionNameChange = (e: React.ChangeEvent<HTMLInputElement>, value: string | number) => {
    this.setState({ actionNameValue: value as string });
  };

  handleFetchSqlCode = async () => {
    this.setState({ isLoadingSqlCode: true });
    try {
      const res = await DatasourceService.GetGenericSelectDataSql(
        this.props.datasourceId,
        this.props.genericSelectData as IGenericSelectDataModel
      );
      const data = res.data;
      if (data) {
        this.setState({ generatedSqlCode: data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoadingSqlCode: false });
    }
  };

  handleTabChange = async (value: string) => {
    this.setState({ activeTab: value });
    if (value === "codeview") {
      await this.handleFetchSqlCode();
    }
  };

  getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "unknown":
        return <KuikaSvg name="string" type="action-icons" />;
      case "boolean":
        return <KuikaSvg name="boolean" type="action-icons" />;
      case "bytea":
        return <KuikaSvg name="decimal" type="action-icons" />;
      case "byte":
        return <KuikaSvg name="decimal" type="action-icons" />;
      case "date":
        return <KuikaSvg name="date&time" type="action-icons" />;
      case "datetime":
        return <KuikaSvg name="date&time" type="action-icons" />;
      case "location":
        return <KuikaSvg name="location" type="action-icons" />;
      case "filecontent":
        return <KuikaSvg name="file-content" type="action-icons" />;
      case "datetimeoffset":
        return <KuikaSvg name="datetimeoffset" type="action-icons" />;
      case "decimal":
        return <KuikaSvg name="decimal" type="action-icons" />;
      case "double":
        return <KuikaSvg name="double" type="action-icons" />;
      case "float":
        return <KuikaSvg name="double" type="action-icons" />;
      case "varbinary":
        return <KuikaSvg name="double" type="action-icons" />;
      case "guid":
        return <KuikaSvg name="guid" type="action-icons" />;
      case "int16":
        return <KuikaSvg name="int16" type="action-icons" />;
      case "int32":
        return <KuikaSvg name="int32" type="action-icons" />;
      case "int64":
        return <KuikaSvg name="int64" type="action-icons" />;
      case "single":
        return <KuikaSvg name="double" type="action-icons" />;
      case "string":
        return <KuikaSvg name="string" type="action-icons" />;
      case "image":
        return <KuikaSvg name="image" type="action-icons" />;
      case "int":
        return <KuikaSvg name="int32" type="action-icons" />;
      case "bigint":
        return <KuikaSvg name="int64" type="action-icons" />;
      case "smallint":
        return <KuikaSvg name="int16" type="action-icons" />;
      case "nvarchar":
        return <KuikaSvg name="string" type="action-icons" />;
      case "uniqueidentifier":
        return <KuikaSvg name="guid" type="action-icons" />;
      case "bit":
        return <KuikaSvg name="boolean" type="action-icons" />;
      default:
        return <KuikaSvg name="string" type="action-icons" />;
    }
  };

  renderTabLabel = (label: string, hasError: boolean) => {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span>{label}</span>
        {hasError && <KuikaSvg name="exclamation-circle" width={16} height={16} color="var(--red-5)" />}
      </div>
    );
  };

  render() {
    const { genericSelectData } = this.props;
    if (!genericSelectData) return <></>;
    const tables = this.getTables();

    return (
      <>
        {this.state.isAddJoinModalVisible && (
          <AddJoinModalComponent
            tables={tables}
            tableName={this.state.addJoinTableName}
            tableSchema={this.state.addJoinTableSchema}
            closeModal={() => this.setState({ isAddJoinModalVisible: false })}
            handleAddJoins={this.handleAddJoins}
            selectDataTables={genericSelectData.tables}
            selectDataRelations={genericSelectData.relations || []}
          />
        )}
        {this.state.isConvertToSqlConfirmationVisible && (
          <ConvertToSqlConfirmationModal
            onOk={this.convertToSql}
            onCancel={() =>
              this.setState({
                isConvertToSqlConfirmationVisible: false,
                sqlResult: ""
              })
            }
          />
        )}
        <div
          className="generic-sql-panel__container"
          // Fills whatever it was given, in the sheet and in the Datasources tool alike. It used
          // to measure the embedded case off the viewport, and that constant counted chrome the
          // card no longer has — leaving the panel 74px above the card's floor.
          style={{ maxHeight: "100%", minHeight: "100%" }}
        >
          <div
            className={`generic-sql-panel__sidebar ${
              this.props.isModal ? "generic-sql-panel__sidebar--modal" : "generic-sql-panel__sidebar--non-modal"
            }`}
          >
            {this.props.isModal && <KuikaMenuItem title={<KuikaTextBold>Select Action</KuikaTextBold>} />}
            <SourcePanelComponent
              genericSelectData={genericSelectData}
              tables={tables}
              handleSelectTable={this.handleSelectTable}
              isDropdownVisible={this.state.isSourceDropdownVisible}
              setDropdownVisibility={(val) => this.setState({ isSourceDropdownVisible: val })}
            />
            <TableListComponent
              genericSelectData={genericSelectData}
              tables={tables}
              handleAddJoinClick={(tableName, tableSchema) =>
                this.setState({
                  addJoinTableName: tableName,
                  addJoinTableSchema: tableSchema,
                  isAddJoinModalVisible: true
                })
              }
              handleDelete={this.updateSelectData}
              getTypeIcon={this.getTypeIcon}
              onSelectSourceClick={() => this.setState({ isSourceDropdownVisible: true })}
              expandedKeys={this.state.expandedTableKeys}
              onExpandedKeysChange={(keys) => this.setState({ expandedTableKeys: keys })}
            />
            <div className="generic-sql-panel__badge-legend">
              <KuikaBadge color="blue" content="ORIGIN" />
              <KuikaBadge color="purple" content="PARENT" />
              <KuikaBadge color="orange" content="CHILD" />
            </div>
          </div>
          <div
            // The column's own `--gap-xl` floor, matching its head. This used to be zeroed while
            // there was at most one table, back when the panel stopped short of the card and
            // that shortfall stood in for the gap.
            className="generic-sql-panel__main-content"
          >
            {/* The dialog's name row. In the Datasources tool the editor's bar names the action,
                with the pencil beside its chip, so the panel draws no second one. */}
            {this.props.isModal && (
              <div className="generic-sql-panel__header-row">
                {!this.state.isActionNameEditing ? (
                  <div className="kuika-flex-center--gap-xs">
                    <KuikaTextMedium
                      data-testid="selectActionNameLabel"
                      style={this.props.nameHasError ? { color: "var(--red-5)" } : undefined}
                    >
                      {this.props.actionName || "Select Action Name"}
                    </KuikaTextMedium>
                    <KuikaButton isIcon onClick={this.handleActionNameClick} type="tertiary" color="neutral">
                      <KuikaSvg name="edit" width={16} height={16} />
                    </KuikaButton>
                  </div>
                ) : (
                  <div className="kuika-flex-center--gap-xs">
                    <KuikaInput
                      data-testid="selectActionNameInput"
                      value={this.state.actionNameValue}
                      onChange={this.handleActionNameChange}
                      onBlur={() => this.handleActionNameSave()}
                      placeholder="Select Action name"
                      autoFocus
                      className="generic-sql-panel__action-name-input"
                      size="sm"
                      error={this.props.nameHasError}
                    />
                    <KuikaButton
                      isIcon
                      data-testid="selectActionNameCancelButton"
                      type="tertiary"
                      color="neutral"
                      onClick={this.handleActionNameCancel}
                    >
                      <KuikaSvg name="x" width={16} height={16} />
                    </KuikaButton>
                    <KuikaButton
                      data-testid="selectActionNameApproveButton"
                      isIcon
                      type="tertiary"
                      onClick={this.handleActionNameSave}
                    >
                      <KuikaSvg name="check" width={16} height={16} />
                    </KuikaButton>
                  </div>
                )}
              </div>
            )}
            <div className="generic-sql-panel__tabs-container">
              <div className="generic-sql-panel__tabs-row">
                <div className="kuika-flex-center--gap-m">
                  <KuikaTabs
                    options={[
                      {
                        value: "relations",
                        label: this.renderTabLabel(
                          "Relations",
                          this.isRelationsDataHasValidationError(genericSelectData)
                        )
                      },
                      {
                        value: "fields",
                        label: this.renderTabLabel(
                          "Fields",
                          this.isFieldsDataHasValidationError(genericSelectData) ||
                            this.isCalculatedFieldsDataHasValidationError(genericSelectData)
                        )
                      },
                      {
                        value: "filter",
                        label: this.renderTabLabel("Filter", this.isWhereDataHasValidationError(genericSelectData))
                      },
                      {
                        value: "sort",
                        label: this.renderTabLabel("Sort order", this.isSortDataHasValidationError(genericSelectData))
                      },
                      {
                        value: "preview",
                        label: "Preview"
                      },
                      {
                        value: "codeview",
                        label: "Code View"
                      }
                    ]}
                    value={this.state.activeTab}
                    onChange={this.handleTabChange}
                    disabled={genericSelectData.tables.length === 0}
                  />
                  {this.state.activeTab === "preview" &&
                    !(
                      this.state.testDataSource.length === 0 ||
                      (this.state.testDataSource.length === 1 && _.isEmpty(this.state.testDataSource[0]))
                    ) && (
                      <KuikaButton onClick={this.handleClickTest} type="secondary" size="sm">
                        TEST
                      </KuikaButton>
                    )}
                </div>
                <div className="kuika-flex-center--gap-m">
                  <div className="generic-sql-panel__enable-pagination">
                    <KuikaSmallText>Enable Pagination</KuikaSmallText>
                    <KuikaSwitch
                      checked={genericSelectData.enablePagination ?? false}
                      onChange={(val) => {
                        this.updateSelectData({
                          ...genericSelectData,
                          enablePagination: val
                        });
                      }}
                    />
                  </div>
                  <KuikaButton
                    onClick={this.handleClickConvertToSql}
                    type="text"
                    size="sm"
                    data-testid="selectActionConvertButton"
                    className="generic-sql-panel__convert-btn"
                  >
                    CONVERT
                  </KuikaButton>
                </div>
              </div>
              <div
                className="generic-sql-panel__tab-content"
                style={{
                  overflowY: "auto",
                  paddingLeft: this.state.activeTab === "filter" ? "8px" : "0"
                }}
              >
                {this.state.activeTab === "relations" && (
                  <JoinTableComponent
                    isModal={this.props.isModal}
                    selectData={genericSelectData}
                    updateSelectData={this.updateSelectData}
                    setDropdownVisibility={(val) => this.setState({ isSourceDropdownVisible: val })}
                    handleAddJoinClick={(tableName, tableSchema) =>
                      this.setState({
                        addJoinTableName: tableName,
                        addJoinTableSchema: tableSchema,
                        isAddJoinModalVisible: true
                      })
                    }
                  />
                )}
                {this.state.activeTab === "fields" && genericSelectData.tables.length > 0 && (
                  <FieldsComponent
                    isModal={this.props.isModal}
                    tables={tables}
                    selectData={genericSelectData}
                    updateSelectData={this.updateSelectData}
                    getTypeIcon={this.getTypeIcon}
                  />
                )}
                {this.state.activeTab === "filter" && genericSelectData.tables.length > 0 && (
                  <FilterComponent
                    isModal={this.props.isModal}
                    tables={tables}
                    selectData={genericSelectData}
                    updateSelectData={this.updateSelectData}
                    getTypeIcon={this.getTypeIcon}
                  />
                )}
                {this.state.activeTab === "sort" && genericSelectData.tables.length > 0 && (
                  <SortOrderComponent
                    isModal={this.props.isModal}
                    tables={tables}
                    selectData={genericSelectData}
                    updateSelectData={this.updateSelectData}
                    getTypeIcon={this.getTypeIcon}
                    enablePagination={genericSelectData.enablePagination ?? false}
                  />
                )}
                {this.state.activeTab === "preview" && genericSelectData.tables.length > 0 && (
                  <TablePreviewComponent
                    testDataSource={this.state.testDataSource}
                    testColumns={this.state.testColumns}
                    onTestClick={this.handleClickTest}
                    hasTested={this.state.hasTested}
                    isLoading={this.state.isTesting}
                  />
                )}
                {this.state.activeTab === "codeview" && genericSelectData.tables.length > 0 && (
                  <KuikaSpinnerSmall spinning={this.state.isLoadingSqlCode} size="small">
                    <CodeViewComponent
                      datasourceId={this.props.datasourceId}
                      selectData={genericSelectData}
                      sqlCode={this.state.generatedSqlCode}
                    />
                  </KuikaSpinnerSmall>
                )}
              </div>
            </div>
            {this.props.footer && this.props.footer}
          </div>
        </div>
      </>
    );
  }
}
const genericSqlPanel = withSD<IGenericSqlPanelProps>(GenericSqlPanel);
export { genericSqlPanel as GenericSqlPanelComponent };
